import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { 
  FileCheck, 
  CheckCircle, 
  Clock, 
  AlertTriangle,
  Upload,
  Loader2,
  X,
  Eye,
  FileText,
  AlertCircle,
  ShieldCheck,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useNavigate } from 'react-router-dom';

const documentTypes = [
  { value: 'creation_authorization', label: "Autorisation de création", required: true, description: "Agrément de création délivré par le Ministère" },
  { value: 'operating_license', label: "Autorisation d'exercice", required: true, description: "Autorisation d'ouverture et d'exploitation" },
  { value: 'tax_documents', label: 'Documents fiscaux', required: false, description: "Attestation de non redevance ou patente" },
];

const FacilityVerification = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [facilityProfile, setFacilityProfile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    document_type: '',
    title: '',
    file: null
  });

  const fetchData = useCallback(async () => {
    try {
      const [profileRes, docsRes] = await Promise.all([
        api.get('/facility/profile'),
        api.get('/facility/documents')
      ]);
      
      setFacilityProfile(profileRes.data);
      setDocuments(docsRes.data.documents || []);
    } catch (error) {
      console.log('Loading facility data...');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    if (!uploadForm.file || !uploadForm.document_type || !uploadForm.title) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      
      const uploadRes = await api.post('/facility/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const fileUrl = uploadRes.data.url;
      
      await api.post('/facility/documents', {
        document_type: uploadForm.document_type,
        title: uploadForm.title,
        file_url: fileUrl
      });

      toast.success('Document téléchargé avec succès');
      setShowUploadModal(false);
      setUploadForm({ document_type: '', title: '', file: null });
      fetchData();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement');
    } finally {
      setUploading(false);
    }
  };

  const getDocumentStatusBadge = (status) => {
    const styles = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'En attente de validation' },
      approved: { bg: 'bg-green-100', text: 'text-green-700', label: 'Approuvé' },
      rejected: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejeté' }
    };
    const style = styles[status] || styles.pending;
    return (
      <Badge className={`${style.bg} ${style.text}`}>
        {style.label}
      </Badge>
    );
  };

  const getRequiredDocumentStatus = (type) => {
    const doc = documents.find(d => d.document_type === type);
    if (!doc) return 'missing';
    return doc.status;
  };

  // Calculate progress
  const requiredDocs = documentTypes.filter(d => d.required);
  const uploadedRequiredDocs = requiredDocs.filter(d => {
    const status = getRequiredDocumentStatus(d.value);
    return status !== 'missing';
  });
  const approvedDocs = requiredDocs.filter(d => getRequiredDocumentStatus(d.value) === 'approved');
  const progress = requiredDocs.length > 0 ? (approvedDocs.length / requiredDocs.length) * 100 : 0;

  // Determine overall KYC status
  const getKYCStatus = () => {
    if (facilityProfile?.is_verified) return 'verified';
    if (approvedDocs.length === requiredDocs.length) return 'verified';
    if (uploadedRequiredDocs.length === requiredDocs.length) return 'submitted';
    if (documents.length > 0) return 'in_progress';
    return 'not_started';
  };

  const kycStatus = getKYCStatus();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-[#E53935] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 px-4 md:px-0" data-testid="facility-verification-page">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate('/facility/dashboard')}
        className="mb-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Retour au tableau de bord
      </Button>

      {/* Header */}
      <div className="bg-gradient-to-r from-[#1A237E] to-[#3949AB] rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Vérification KYC
            </h1>
            <p className="text-white/80 text-sm">
              Soumettez vos documents légaux pour vérification
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-white/10 rounded-lg p-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-white/80">Progression</span>
            <span className="text-sm font-medium">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2 bg-white/20" />
          <p className="text-xs text-white/60 mt-2">
            {approvedDocs.length} / {requiredDocs.length} documents approuvés
          </p>
        </div>
      </div>

      {/* Status Card */}
      {kycStatus === 'verified' ? (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-green-800">Établissement vérifié</h2>
                <p className="text-green-700">
                  Félicitations ! Votre établissement est vérifié et visible dans l&apos;annuaire DOCKHTA.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : kycStatus === 'submitted' ? (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <Clock className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-blue-800">Vérification en cours</h2>
                <p className="text-blue-700">
                  Vos documents sont en cours d&apos;examen par notre équipe. Délai habituel : 24-48 heures.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-yellow-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-yellow-800">Vérification requise</h2>
                <p className="text-yellow-700">
                  Soumettez les documents requis pour activer votre compte et apparaître dans l&apos;annuaire.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Required Documents Checklist */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-[#1A237E]" />
                Documents requis
              </CardTitle>
              <CardDescription>
                Téléchargez les documents officiels pour la vérification
              </CardDescription>
            </div>
            {kycStatus !== 'verified' && (
              <Button 
                onClick={() => setShowUploadModal(true)}
                className="bg-[#E53935] hover:bg-[#C62828]"
                data-testid="upload-document-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Télécharger
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {documentTypes.map((docType) => {
              const status = getRequiredDocumentStatus(docType.value);
              const uploadedDoc = documents.find(d => d.document_type === docType.value);
              
              return (
                <div 
                  key={docType.value} 
                  className={`p-4 rounded-xl border-2 transition-all ${
                    status === 'approved' ? 'border-green-200 bg-green-50' :
                    status === 'pending' ? 'border-yellow-200 bg-yellow-50' :
                    status === 'rejected' ? 'border-red-200 bg-red-50' :
                    'border-slate-200 bg-slate-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        status === 'approved' ? 'bg-green-100' :
                        status === 'pending' ? 'bg-yellow-100' :
                        status === 'rejected' ? 'bg-red-100' :
                        'bg-slate-200'
                      }`}>
                        {status === 'approved' ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : status === 'pending' ? (
                          <Clock className="w-5 h-5 text-yellow-600" />
                        ) : status === 'rejected' ? (
                          <AlertCircle className="w-5 h-5 text-red-600" />
                        ) : (
                          <FileText className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">
                          {docType.label}
                          {docType.required && <span className="text-red-500 ml-1">*</span>}
                        </h3>
                        <p className="text-sm text-slate-500">{docType.description}</p>
                        {uploadedDoc?.title && (
                          <p className="text-xs text-slate-400 mt-1">
                            Fichier : {uploadedDoc.title}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {status !== 'missing' ? (
                        <>
                          {getDocumentStatusBadge(status)}
                          {uploadedDoc?.file_url && (
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => {
                                const fileUrl = uploadedDoc.file_url;
                                const isPDF = fileUrl.toLowerCase().includes('.pdf');
                                if (isPDF) {
                                  window.open(`https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}`, '_blank');
                                } else {
                                  window.open(fileUrl, '_blank');
                                }
                              }}
                              title="Voir le document"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                        </>
                      ) : (
                        <Badge variant="outline" className="text-slate-500">Non fourni</Badge>
                      )}
                    </div>
                  </div>
                  {status === 'rejected' && uploadedDoc?.reviewer_notes && (
                    <div className="mt-3 p-3 bg-red-100 rounded-lg">
                      <p className="text-sm text-red-700">
                        <strong>Motif du rejet :</strong> {uploadedDoc.reviewer_notes}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Optional Documents */}
          {documents.filter(d => !documentTypes.find(dt => dt.value === d.document_type)?.required).length > 0 && (
            <div className="mt-6 pt-6 border-t">
              <h4 className="font-medium text-slate-700 mb-3">Documents supplémentaires</h4>
              <div className="space-y-2">
                {documents.filter(d => !documentTypes.find(dt => dt.value === d.document_type)?.required).map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-slate-400" />
                      <span className="text-sm">{doc.title}</span>
                    </div>
                    {getDocumentStatusBadge(doc.status)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Help Section */}
      <Card className="border-slate-200">
        <CardContent className="p-6">
          <h3 className="font-semibold text-slate-900 mb-3">Besoin d&apos;aide ?</h3>
          <p className="text-sm text-slate-600 mb-4">
            Si vous avez des questions concernant la vérification de votre établissement, 
            contactez notre équipe support.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" size="sm">
              📧 support@dockhta.com
            </Button>
            <Button variant="outline" size="sm">
              📞 +237 695 06 57 08
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Upload Document Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ajouter un document légal</DialogTitle>
            <DialogDescription>
              Téléchargez vos documents officiels pour la vérification KYC
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleUploadDocument} className="space-y-4">
            <div>
              <Label>Type de document *</Label>
              <Select
                value={uploadForm.document_type}
                onValueChange={(v) => setUploadForm(p => ({ ...p, document_type: v }))}
              >
                <SelectTrigger data-testid="document-type-select">
                  <SelectValue placeholder="Sélectionnez le type" />
                </SelectTrigger>
                <SelectContent>
                  {documentTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label} {type.required && '*'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Titre du document *</Label>
              <Input
                value={uploadForm.title}
                onChange={(e) => setUploadForm(p => ({ ...p, title: e.target.value }))}
                placeholder="Ex: Licence d'exploitation 2024"
                data-testid="document-title-input"
              />
            </div>

            <div>
              <Label>Fichier *</Label>
              <div className="mt-2 border-2 border-dashed border-slate-200 rounded-xl p-6 text-center">
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setUploadForm(p => ({ ...p, file: e.target.files?.[0] || null }))}
                  className="hidden"
                  id="kyc-file-upload"
                  data-testid="document-file-input"
                />
                <label htmlFor="kyc-file-upload" className="cursor-pointer">
                  {uploadForm.file ? (
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-slate-700">{uploadForm.file.name}</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setUploadForm(p => ({ ...p, file: null }));
                        }}
                        className="text-slate-400 hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-slate-500 text-sm">
                        Cliquez pour sélectionner un fichier
                      </p>
                      <p className="text-slate-400 text-xs mt-1">
                        PDF, JPG, PNG (max 10MB)
                      </p>
                    </>
                  )}
                </label>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowUploadModal(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={uploading || !uploadForm.document_type || !uploadForm.title || !uploadForm.file}
                className="flex-1 bg-[#E53935] hover:bg-[#C62828]"
                data-testid="upload-document-submit"
              >
                {uploading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Upload className="w-5 h-5 mr-2" />
                    Télécharger
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FacilityVerification;
